import React from 'react';
import { createPortal } from 'react-dom';

/**
 * Modal con instrucciones para instalar la PWA
 * Muestra diferentes instrucciones según el dispositivo (iOS o Android)
 */
const PWAInstallModal = ({ isOpen, onClose, isIOS, isAndroid, isInstallable, onInstall }) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            Instalar Alquilalo!
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Contenido */}
        <div className="p-6">
          {/* Instrucciones para Android */}
          {isAndroid && !isIOS && (
            <div className="space-y-4">
              {isInstallable ? (
                <>
                  <p className="text-gray-700 mb-4">
                    Instala Alquilalo! en tu dispositivo Android para acceder más rápido.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800 font-semibold mb-2">
                      Opción 1: Instalación automática
                    </p>
                    <button
                      onClick={onInstall}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                    >
                      Instalar Ahora
                    </button>
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm text-gray-800 font-semibold mb-3">
                      Opción 2: Instalación manual
                    </p>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                      <li>Abre el menú del navegador (tres puntos en la esquina superior derecha)</li>
                      <li>Selecciona <strong>"Instalar app"</strong> o <strong>"Agregar a pantalla de inicio"</strong></li>
                      <li>Confirma la instalación tocando <strong>"Instalar"</strong></li>
                      <li>¡Listo! La app aparecerá en tu pantalla de inicio</li>
                    </ol>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-700 mb-4">
                    Sigue estos pasos para instalar Alquilalo! en tu dispositivo Android:
                  </p>
                  <ol className="list-decimal list-inside space-y-3 text-sm text-gray-700">
                    <li>
                      <strong>Abre el menú del navegador</strong>
                      <br />
                      <span className="text-gray-600">
                        Toca los tres puntos (⋮) en la esquina superior derecha
                      </span>
                    </li>
                    <li>
                      <strong>Selecciona "Instalar app"</strong>
                      <br />
                      <span className="text-gray-600">
                        Busca la opción "Instalar app" o "Agregar a pantalla de inicio"
                      </span>
                    </li>
                    <li>
                      <strong>Confirma la instalación</strong>
                      <br />
                      <span className="text-gray-600">
                        Toca "Instalar" en el diálogo de confirmación
                      </span>
                    </li>
                    <li>
                      <strong>¡Listo!</strong>
                      <br />
                      <span className="text-gray-600">
                        La app aparecerá en tu pantalla de inicio y podrás abrirla como una app nativa
                      </span>
                    </li>
                  </ol>
                </>
              )}
            </div>
          )}

          {/* Instrucciones para iOS */}
          {isIOS && (
            <div className="space-y-4">
              <p className="text-gray-700 mb-4">
                Sigue estos pasos para instalar Alquilalo! en tu iPhone o iPad:
              </p>
              <ol className="list-decimal list-inside space-y-3 text-sm text-gray-700">
                <li>
                  <strong>Toca el botón de compartir</strong>
                  <br />
                  <span className="text-gray-600">
                    Busca el ícono de compartir (cuadrado con flecha hacia arriba) en la barra inferior de Safari
                  </span>
                </li>
                <li>
                  <strong>Selecciona "Agregar a pantalla de inicio"</strong>
                  <br />
                  <span className="text-gray-600">
                    Desplázate hacia abajo en el menú y toca "Agregar a pantalla de inicio"
                  </span>
                </li>
                <li>
                  <strong>Personaliza el nombre (opcional)</strong>
                  <br />
                  <span className="text-gray-600">
                    Puedes cambiar el nombre si lo deseas, luego toca "Agregar"
                  </span>
                </li>
                <li>
                  <strong>¡Listo!</strong>
                  <br />
                  <span className="text-gray-600">
                    La app aparecerá en tu pantalla de inicio con el ícono de Alquilalo!
                  </span>
                </li>
              </ol>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-yellow-800">
                  <strong>💡 Tip:</strong> Asegúrate de usar Safari para instalar la app. 
                  Chrome y otros navegadores en iOS no permiten la instalación de PWAs.
                </p>
              </div>
            </div>
          )}

          {/* Instrucciones genéricas para otros dispositivos */}
          {!isAndroid && !isIOS && (
            <div className="space-y-4">
              <p className="text-gray-700 mb-4">
                Instala Alquilalo! en tu dispositivo para una mejor experiencia:
              </p>
              {isInstallable ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <button
                    onClick={onInstall}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                  >
                    Instalar Ahora
                  </button>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    Busca el ícono de instalación en la barra de direcciones de tu navegador 
                    o en el menú del navegador para instalar la app.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Beneficios de instalar */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm font-semibold text-gray-900 mb-2">
              ✨ Beneficios de instalar la app:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
              <li>Acceso rápido desde tu pantalla de inicio</li>
              <li>Funciona sin conexión a internet (modo offline)</li>
              <li>Notificaciones push para actualizaciones importantes</li>
              <li>Experiencia similar a una app nativa</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PWAInstallModal;
